import { useEffect, useMemo, useState } from "react";
import { discoverClient } from "../discoverClient";
import { normalizeDiscoveryCatalogue } from "./discoveryCatalogue";
import {
  EDITORIAL_LISTING_IDS,
  HERO_CARD_COUNT,
  HERO_CARD_DISPLAY,
  HERO_DEMOS,
  SPOTLIGHTS,
  type HeroDemo,
  type Spotlight,
} from "./discoveryEditorial";
import type { DiscoveryCardModel } from "./types";

export interface HeroAppCard {
  listingId: string;
  card: DiscoveryCardModel;
  /** A shorter display name when the listing title carries a version. */
  name?: string;
  /** The app's own icon, when the listing's icon is not a usable mark. */
  icon?: string;
}

export interface EditorialSlots {
  demos: readonly (HeroDemo & { card: DiscoveryCardModel })[];
  /** The wide-screen hero's apps; null until popularity has answered. */
  cards: readonly HeroAppCard[] | null;
  /** The Featured row's features this environment has, in editorial order. */
  spotlights: readonly (Spotlight & { card: DiscoveryCardModel })[];
}

const listingId = (card: DiscoveryCardModel) =>
  card.selection.kind === "discover" ? card.selection.id : null;

/**
 * Resolves the editorial slots against listings this environment really has.
 * Loaded catalogue pages answer first; any other editorial listing is read
 * once by ID. A listing that is absent (or unreadable) simply leaves its slot
 * out — editorial picks are optional, the catalogue below is not.
 *
 * The wide-screen hero is not curated: it shows the most popular apps that
 * have a preview video, read once with `sort=popular`. If that read fails
 * (an API without the sort), the catalogue's own order stands in.
 *
 * Nothing is read until `enabled` (the grid is showing and its first page has
 * answered), so opening an app never waits behind editorial requests. The
 * popularity read also waits for `heroCards`, so phones never make it.
 */
export function useDiscoveryEditorial(
  entries: readonly DiscoveryCardModel[],
  enabled: boolean,
  { heroCards = false }: { heroCards?: boolean } = {},
): EditorialSlots {
  const [fetched, setFetched] = useState<ReadonlyMap<string, DiscoveryCardModel | null>>(new Map());
  const [popular, setPopular] = useState<readonly DiscoveryCardModel[] | "failed" | null>(null);
  const readPopular = enabled && heroCards && popular === null;
  useEffect(() => {
    if (!readPopular) return;
    const controller = new AbortController();
    discoverClient.page({ sort: "popular", limit: 24 }, controller.signal).then(
      (page) => { if (!controller.signal.aborted) setPopular(normalizeDiscoveryCatalogue(page.apps, [])); },
      () => { if (!controller.signal.aborted) setPopular("failed"); },
    );
    return () => controller.abort();
  }, [readPopular]);
  const loaded = useMemo(() => {
    const byId = new Map<string, DiscoveryCardModel>();
    for (const card of entries) {
      const id = listingId(card);
      if (id && EDITORIAL_LISTING_IDS.includes(id)) byId.set(id, card);
    }
    return byId;
  }, [entries]);
  const missing = EDITORIAL_LISTING_IDS.filter((id) => !loaded.has(id) && !fetched.has(id));
  const missingKey = enabled ? missing.join(",") : "";
  useEffect(() => {
    if (!missingKey) return;
    const controller = new AbortController();
    void Promise.all(missingKey.split(",").map(async (id) => {
      try {
        const { app } = await discoverClient.detail(id, controller.signal);
        return [id, normalizeDiscoveryCatalogue([app], [])[0] ?? null] as const;
      } catch {
        return [id, null] as const;
      }
    })).then((results) => {
      if (controller.signal.aborted) return;
      setFetched((current) => new Map([...current, ...results]));
    });
    return () => controller.abort();
  }, [missingKey]);
  return useMemo(() => {
    const find = (id: string) => loaded.get(id) ?? fetched.get(id) ?? null;
    const withCard = <T extends { listingId: string }>(item: T) => {
      const card = find(item.listingId);
      return card ? [{ ...item, card }] : [];
    };
    const ranked = popular === "failed" ? entries : popular;
    return {
      demos: HERO_DEMOS.flatMap(withCard),
      cards: ranked && ranked.flatMap((card) => {
        const id = listingId(card);
        return id && card.recordedPreview ? [{ listingId: id, card, ...HERO_CARD_DISPLAY[id] }] : [];
      }).slice(0, HERO_CARD_COUNT),
      spotlights: SPOTLIGHTS.flatMap(withCard),
    };
  }, [loaded, fetched, popular, entries]);
}
